import { ProgressGPTReplyTypeEnum } from 'api/enums';
import { IProgressGPTReplyModel } from 'api/models';
import { Divider } from 'library/divider';
import { Skelton } from 'library/skelton';
import groupBy from 'lodash.groupby';
import useAuthSelector from 'modules/shared/hooks/useAuthSelector';
import moment from 'moment-timezone';
import { FC, useLayoutEffect, useMemo, useRef } from 'react';
import useDeferredLoading from '../../hooks/useDeferredLoading';
import BotReply from '../BotReply/BotReply';
import ProgressGPTContainer from '../ProgressGPTContainer/ProgressGPTContainer';
import UserReply from '../UserReply/UserReply';

interface ChatRepliesProps {
    replies: IProgressGPTReplyModel[];
    isLoading: boolean;
    typingId: string;
}
const ChatReplies: FC<ChatRepliesProps> = (props) => {
    const { replies, isLoading, typingId } = props;

    const { user } = useAuthSelector();

    const endOfMessagesRef = useRef<HTMLDivElement>(null);

    // const chatBoxRef = useRef(null);

    const [showAnalysingMessage] = useDeferredLoading(isLoading, 4000);

    const scrollToBottom = () => {
        endOfMessagesRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useLayoutEffect(() => {
        scrollToBottom();
    }, [replies, isLoading]);

    // useLayoutEffect(() => {
    //     if (chatBoxRef.current) {
    //         chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    //     }
    // }, [replies]);

    const groupedReplies = useMemo(() => {
        const repliesMap = groupBy(replies, (item) => {
            const date = moment.tz(item.createdAt, user.preferences.timezone);

            return date.calendar(null, {
                lastDay: '[Yesterday · ' + date.format('DD MMM') + ']',
                sameDay: '[Today · ' + date.format('DD MMM') + ']',
                lastWeek: () => {
                    return '[' + date.format('DD MMM, YYYY') + ']';
                },
                sameElse: () => {
                    return '[' + date.format('DD MMM, YYYY') + ']';
                },
            });
        });

        return Object.keys(repliesMap).map((key) => ({ date: key, replies: repliesMap[key] }));
    }, [replies, user.preferences.timezone]);

    return (
        // ref={chatBoxRef}
        <ProgressGPTContainer className="relative py-4">
            <div className="space-y-3">
                {groupedReplies.map((row, i) => (
                    <div key={i} className="space-y-3">
                        <div className="relative flex items-center justify-center py-2">
                            <Divider className="absolute w-[200px]" />
                            <div className="relative inline-flex justify-center items-center text-center bg-card-secondary-color text-grayscale-500 text-xs h-6 mx-auto px-[10px] rounded-full">
                                {row.date}
                            </div>
                        </div>

                        {row.replies.map((reply) => (
                            <div key={reply._id}>
                                {reply.from === ProgressGPTReplyTypeEnum.BOT ? (
                                    <BotReply message={reply.message} isTyping={typingId === reply._id} />
                                ) : (
                                    <UserReply message={reply.message} />
                                )}
                            </div>
                        ))}
                    </div>
                ))}
            </div>

            {isLoading && (
                <div className="mt-4">
                    {showAnalysingMessage && (
                        <div className="relative px-4 pt-2 text-grayscale-900 font-medium italic text-sm">
                            Analysing...
                        </div>
                    )}
                    <div className="px-4 max-h-full w-full  overflow-y-auto toolbar-scrollbar">
                        <div className="space-y-3">
                            <div>
                                <div className="flex gap-3">
                                    <div className="flex-1">
                                        <Skelton width="70%" />
                                        <Skelton width="100%" />
                                        {/* <Skelton width="85%" /> */}
                                        {/* <Skelton width="60%" /> */}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div ref={endOfMessagesRef} />
        </ProgressGPTContainer>
    );
};

export default ChatReplies;
